import React, { useCallback } from 'react';
import moment from 'moment';
import t from 'react-translate';
import { useSelector } from 'react-redux';

import { Team } from 'redux/schemas/models/team';
import { User } from 'redux/schemas/models/my-account';
import { Submission } from 'redux/schemas/models/submissions';
import { ExerciseOwner } from 'redux/schemas/models/exercise';
import { getCurrentInstitution } from 'redux/selectors/institutions';

import { NvUserAvatar } from 'components/nv-user-avatar';
import NvTeamAvatar from 'components/nv-team-avatar';
import { DAY_THRESHOLD, HOUR_THRESHOLD, MINUTE_THRESHOLD, useApprovalStatus } from 'shared/components/submission/utils';

type SubmissionHeaderProps = {
  submission: Submission;
  showStatus?: boolean,
  statusClass?: string,
  statusContent?: JSX.Element,
  showApprovalStatus?: boolean
  showApprovalPendingTime?: boolean
};

const SubmissionHeader: React.FC<SubmissionHeaderProps> = ({
  submission,
  showStatus,
  statusClass,
  statusContent,
  showApprovalStatus,
  showApprovalPendingTime,
}) => {
  const {
    exercise,
    owner,
    ownerType,
    body,
    submitting,
    coverPhotoUrl,
  } = submission;

  const institution = useSelector(getCurrentInstitution);
  const { approvalStatusFlavor, isPendingApproval } = useApprovalStatus(submitting.submissionStatus, exercise?.hardDeadline, exercise?.deadline);

  const pendingTime = useCallback(() => {
    const submissionStatusDate = submitting?.submissionStatusDate;
    const timeDifference = moment().valueOf() - moment(submissionStatusDate).valueOf();

    if (Math.floor(timeDifference / DAY_THRESHOLD)) {
      return t.SUBMISSION_APPROVAL.PENDING.DAYS(Math.floor(timeDifference / DAY_THRESHOLD));
    } if (Math.floor(timeDifference / HOUR_THRESHOLD)) {
      return t.SUBMISSION_APPROVAL.PENDING.HOURS(Math.floor(timeDifference / HOUR_THRESHOLD));
    }
    return t.SUBMISSION_APPROVAL.PENDING.MINUTES(Math.floor(timeDifference / MINUTE_THRESHOLD) || 1);
  }, [submitting?.submissionStatusDate]);

  return (
    <React.Fragment>
      {showStatus && (
        <div className={`status-wrapper ${statusClass}`}>
          {statusContent}
        </div>
      )}
      {!showStatus && !showApprovalPendingTime && showApprovalStatus && exercise?.approvalRequired && (
        <div className={`status-wrapper ${approvalStatusFlavor.bg}`}>
          {t.SUBMISSION_APPROVAL.SUBMISSION_STATUS[approvalStatusFlavor.translateKey]()}
        </div>
      )}
      {!showStatus && showApprovalPendingTime && isPendingApproval && (
        <div className={`status-wrapper ${approvalStatusFlavor.bg}`}>
          {pendingTime()}
        </div>
      )}
      <div
        className='header-main-content'
        aria-label={`${body}, ${t.EXERCISES.VIEW_SUBMISSION_DETAILS(submission.title)}`}
        id={`submission_description_${submission.id}`}
      >
        {coverPhotoUrl
          ? <div className='course-logo' />
          : (
            <div className='submission-header'>
              <div
                className='body'
              >
                {body}
              </div>
            </div>
          )}
      </div>
      <div className='owner-type-info'>
        {ownerType === ExerciseOwner.USER ? (
          <NvUserAvatar
            size='md'
            borderType='round'
            user={owner as User}
            displayName
            inlineName
          />
        ) : (
          <NvTeamAvatar
            center
            size='md'
            team={owner as Team}
            course={exercise?.course}
            institution={institution}
          />
        )}
      </div>
    </React.Fragment>
  );
};

export default SubmissionHeader;
